import React from 'react';
import { Button } from './ui/button';
import { Calendar, RotateCcw } from 'lucide-react';

/**
 * DateRangeControl — the ONE start/end date picker used across every operator
 * data surface (Dashboard, Revenue, P&L, Expenses, …). Two bordered native
 * date inputs with a calendar glyph + a "to" separator + an optional reset.
 *
 * Native <input type="date"> is intentional: it's locale-/keyboard-/mobile-
 * friendly and needs no extra dependency.
 *
 * @param {string} startDate  YYYY-MM-DD
 * @param {string} endDate    YYYY-MM-DD
 * @param {(v: string) => void} onStartChange
 * @param {(v: string) => void} onEndChange
 * @param {() => void} [onReset]
 * @param {boolean} [showReset]
 * @param {string} [minDate] / {string} [maxDate]
 */
export const DateRangeControl = ({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  onReset,
  showReset = false,
  minDate,
  maxDate,
}) => (
  <div className="flex items-center gap-2" data-testid="date-range-control">
    <div className="inline-flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
      <input
        type="date"
        value={startDate}
        min={minDate}
        max={endDate || maxDate}
        onChange={(e) => onStartChange(e.target.value)}
        className="border-none bg-transparent text-sm text-gray-900 focus:outline-none w-[112px]"
        aria-label="Start date"
        data-testid="date-range-start"
      />
      <span className="text-gray-300 text-sm shrink-0">–</span>
      <input
        type="date"
        value={endDate}
        min={startDate || minDate}
        max={maxDate}
        onChange={(e) => onEndChange(e.target.value)}
        className="border-none bg-transparent text-sm text-gray-900 focus:outline-none w-[112px]"
        aria-label="End date"
        data-testid="date-range-end"
      />
    </div>
    {showReset && onReset && (
      <Button
        variant="ghost"
        size="sm"
        onClick={onReset}
        className="text-gray-500 hover:text-gray-700 px-2 shrink-0"
        title="Reset to default range"
        data-testid="date-range-reset"
      >
        <RotateCcw className="w-4 h-4" />
      </Button>
    )}
  </div>
);

export default DateRangeControl;
